import * as React from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
  getFullAddress,
  getLocationLine,
  getMapHref,
  getPhoneHref,
  getPrimaryEmail,
  getPrimaryPhone,
  isBusinessActive,
  PAGE_SIZE,
} from "./shared";

type Props = {
  businesses: any[];
  claimFilter: string;
  onClaimFilterChange: (value: string) => void;
  onPageChange: (value: number) => void;
  onSearchTextChange: (value: string) => void;
  onSelectedBusinessIdChange: (value: number) => void;
  onStateFilterChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onZipFilterChange: (value: string) => void;
  page: number;
  pageCount: number;
  searchText: string;
  selectedBusinessId: number | null;
  stateFilter: string;
  statusFilter: string;
  totalBusinesses: number;
  zipFilter: string;
};

export function BusinessListPanel({
  businesses,
  claimFilter,
  onClaimFilterChange,
  onPageChange,
  onSearchTextChange,
  onSelectedBusinessIdChange,
  onStateFilterChange,
  onStatusFilterChange,
  onZipFilterChange,
  page,
  pageCount,
  searchText,
  selectedBusinessId,
  stateFilter,
  statusFilter,
  totalBusinesses,
  zipFilter,
}: Props) {
  return (
    <Paper
      variant="outlined"
      sx={{
        width: { xs: "100%", lg: 430 },
        borderRadius: 4,
        p: 2,
        flexShrink: 0,
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="h6" fontWeight={700}>
          Businesses
        </Typography>
        <Stack spacing={1}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by business, city, zip, email, phone, URL, or AFH license"
            value={searchText}
            onChange={(event) => onSearchTextChange(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              select
              size="small"
              label="Claim"
              value={claimFilter}
              onChange={(event) => onClaimFilterChange(event.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="claimed">Claimed</MenuItem>
              <MenuItem value="unclaimed">Unclaimed</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(event) => onStatusFilterChange(event.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              label="State"
              value={stateFilter}
              onChange={(event) => onStateFilterChange(event.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
              inputProps={{ maxLength: 2 }}
            />
            <TextField
              size="small"
              label="ZIP"
              value={zipFilter}
              onChange={(event) => onZipFilterChange(event.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
              inputProps={{ maxLength: 10 }}
            />
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Showing {(page - 1) * PAGE_SIZE + (businesses.length ? 1 : 0)}-
          {(page - 1) * PAGE_SIZE + businesses.length} of {totalBusinesses} businesses
        </Typography>
        <Stack spacing={1.1} sx={{ maxHeight: "70vh", overflowY: "auto", pr: 0.5 }}>
          {businesses.map((business: any) => {
            const isSelected = Number(selectedBusinessId) === Number(business?.id);
            const primaryEmail = getPrimaryEmail(business);
            const primaryPhone = getPrimaryPhone(business);
            const mapHref = getMapHref(business);

            return (
              <Card
                key={business?.id}
                variant="outlined"
                onClick={() => onSelectedBusinessIdChange(Number(business?.id || 0))}
                sx={{
                  cursor: "pointer",
                  borderRadius: 3,
                  borderColor: isSelected ? "primary.main" : "divider",
                  boxShadow: isSelected ? "0 0 0 1px rgba(25,118,210,0.12)" : "none",
                  minWidth: 0,
                  minHeight: 132,
                }}
              >
                <CardContent sx={{ pt: 2, pb: 2.75, "&:last-child": { pb: 2.75 } }}>
                  <Stack spacing={1.1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Typography variant="subtitle1" fontWeight={700}>
                        {business?.name || "Unnamed business"}
                      </Typography>
                      <Chip
                        size="small"
                        color={isBusinessActive(business) ? "success" : "default"}
                        variant="outlined"
                        label={isBusinessActive(business) ? "Active" : "Inactive"}
                      />
                    </Stack>
                    {getLocationLine(business) ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        title={getFullAddress(business) || getLocationLine(business)}
                        sx={{ minWidth: 0, width: "100%", display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {getFullAddress(business) || getLocationLine(business)}
                      </Typography>
                    ) : null}
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{
                        width: "100%",
                        justifyContent: "flex-start",
                        flexWrap: "wrap",
                        pt: 1,
                        mt: 0.5,
                        minHeight: 40,
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        "& .MuiIconButton-root.Mui-disabled": {
                          opacity: 0.45,
                          color: "text.secondary",
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        component="a"
                        href={mapHref || undefined}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        aria-label="Open Google Map"
                        disabled={!mapHref}
                      >
                        <PlaceOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        component="a"
                        href={primaryPhone ? getPhoneHref(primaryPhone) : undefined}
                        onClick={(event) => event.stopPropagation()}
                        aria-label="Call business"
                        disabled={!primaryPhone}
                      >
                        <CallRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        component="a"
                        href={primaryEmail ? `mailto:${primaryEmail}` : undefined}
                        onClick={(event) => event.stopPropagation()}
                        aria-label="Email business"
                        disabled={!primaryEmail}
                      >
                        <MailOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
          {!businesses.length ? <Alert severity="info">No businesses matched that search.</Alert> : null}
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Page {page} of {pageCount}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              Previous
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={page >= pageCount}
              onClick={() => onPageChange(Math.min(pageCount, page + 1))}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}
